import { AccountHeaderModal } from 'components/accounts/AccountHeaderModal'
import {
  ClockIcon,
  FlagIcon,
  IconButton,
  MapMarkerIcon,
  PencilAltIcon,
  Pill,
  ProfileHeader,
  useModal,
  useRouter,
} from '@forgeglobal/design-framework'
import { getGravatarUrl } from 'util/GravatarUtil'
import { renderLocation } from 'util/AccountUtils'
import { useAccountHeaderQuery } from 'graphql/bindings/AdminBindings'
import React, { Fragment } from 'react'
import compact from 'lodash/compact'
import moment from 'moment'

export const AccountHeader: React.FC = () => {
  const { match } = useRouter<{
    id: string
  }>()
  const accountId = match.params.id
  const { data } = useAccountHeaderQuery({ variables: { id: accountId } })
  const account = data?.account
  const gravatarUrl = getGravatarUrl(account?.email ?? '')

  const introducer = data?.account?.introducedByRelationships?.[0]
  const introducerBrokerName = introducer?.introduction?.parent_broker_account?.name
  const introducerAccountName = introducer?.parent_account.name
  const introducerName = introducerBrokerName ?? introducerAccountName

  const pills = compact([
    account?.is_account_of_interest && (
      <Pill colorType="green" label="Account of Interest" icon={<FlagIcon />} key={'interest'} />
    ),
    account?.is_shareholder && <Pill variant="outlined" label="Shareholder" key={'shareholder'} />,
    account?.is_investor && (
      <Pill variant="outlined" label="Investor" dividedValue={account?.account_level ?? undefined} key={'investor'} />
    ),
    introducerName && <Pill variant="outlined" label={`Introduced by ${introducerName}`} key={'introduced'} />,
  ])

  const [showModal, hideModal] = useModal(() => {
    return <AccountHeaderModal hideModal={hideModal} accountId={accountId} />
  }, [accountId])

  const renderedLocation = renderLocation({
    city: account?.derived_location?.city,
    state: account?.derived_location?.state,
    country: account?.derived_location?.countryCode,
    source: account?.derived_location?.source,
  })

  let accountOfInterestDetails: React.ReactNode | null = null
  if (account?.is_account_of_interest ?? false) {
    const setBy = account?.account_of_interest_set_by_admin
    if (setBy) {
      accountOfInterestDetails = (
        <Fragment>
          <span>Marked Account of Interest by {setBy.name}</span>
          {account?.account_of_interest_reason ? (
            <Fragment>
              <br />
              <span>{account?.account_of_interest_reason ?? ''}</span>
            </Fragment>
          ) : null}
        </Fragment>
      )
    } else {
      accountOfInterestDetails = (
        <Fragment>
          <span>Automatically marked as an Account of Interest</span>
          <br />
          <span>{account?.account_of_interest_reason ?? ''}</span>
        </Fragment>
      )
    }
  }

  const lastOrderDate = account?.last_order_closed_date
  const orderDateAgo = lastOrderDate && moment(lastOrderDate).fromNow()
  const lastOutreachAt = account?.last_outreach_by_anyone
  const contactedAgo = lastOutreachAt && moment(lastOutreachAt).fromNow()
  const historicalInfo = (
    <Fragment>
      {lastOutreachAt && (
        <Fragment>
          Last outreach {contactedAgo} by {account?.last_outreach_by_anyone_admin?.first_name}{' '}
          {account?.last_outreach_by_anyone_admin?.last_name}
          <br />
        </Fragment>
      )}
      {lastOrderDate && `Last transacted ${orderDateAgo}`}
    </Fragment>
  )

  let linkedinTitle
  if (account?.linkedin_job) {
    linkedinTitle = [account?.linkedin_job.employment_title, account?.linkedin_job.employment_name]
      .filter(field => !!field)
      .join(', ')
  }

  const subtitle = [account?.client_type, linkedinTitle].filter(field => !!field).join(' - ') || null
  const clockContent = (lastOrderDate || lastOutreachAt) && historicalInfo
  const details = compact([
    renderedLocation && {
      icon: <MapMarkerIcon />,
      content: renderedLocation,
    },
    clockContent && {
      icon: <ClockIcon />,
      content: clockContent,
    },
    accountOfInterestDetails && {
      icon: <FlagIcon />,
      content: accountOfInterestDetails,
    },
  ])

  const control = (
    <IconButton data-cypress="edit-account-header-button" onClick={showModal}>
      <PencilAltIcon />
    </IconButton>
  )

  return (
    <ProfileHeader
      title={account?.name ?? ''}
      subtitle={subtitle || ''}
      avatarImg={gravatarUrl}
      details={details}
      secondaryControl={control}>
      {pills}
    </ProfileHeader>
  )
}
